import { useCallback, useEffect, useState } from 'react'
import { useRef } from 'react'
import { useMemo } from 'react'

import { useRevalidator, useFetcher } from '@remix-run/react'
import { captureException } from '@sentry/remix'
import type { z } from 'zod'

import {
  type MessageChunkPayload,
  TicketChannelPayloadSchema,
} from '@server/services/channel/ticketChannelEvents.dto'
import type {
  TicketEventForQA,
  TicketActionsResultsForQA,
  TicketActionResultForQA,
  MessageDetails,
} from '@server/services/ticket/ticket'
import type {
  MessageProps,
  MessageType,
} from '@web/components/message/messages'

export const usePendingMessage = () => {
  const { formData } = useFetcher({ key: 'sendChat' })

  return useMemo(() => {
    const content = formData?.get('message') as string
    const type = (formData?.get('type') ?? 'CUSTOMER') as
      | 'CUSTOMER'
      | 'INTERNAL_USER_NOTE'
    const customerFirstName = formData?.get('customerFirstName') as string
    const customerLastName = formData?.get('customerLastName') as string
    if (!content) {
      return undefined
    }

    return generateNewMessage(
      content,
      type,
      customerFirstName,
      customerLastName,
    )
  }, [formData])
}

const generateNewMessage = (
  content: string,
  messageType: 'CUSTOMER' | 'INTERNAL_USER_NOTE',
  firstName: string,
  lastName: string,
): MessageProps => {
  return {
    id: 'stub',
    content,
    customer: {
      email: 'stub',
      firstName,
      lastName,
    },
    messageType,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

// Currently, getByIdForQ may take >2 seconds in prod for subscribers with many customers (e.g. hundreds of thousands).
// If getByIdForQ takes longer than the polling interval, the request is cancelled and polling appears broken.
const ACTIVE_POLLING_INTERVAL = 5_000
const IDLE_POLLING_INTERVAL = 30_000
const POLLING_TIMEOUT = 120_000

const BOT_MESSAGE_TYPES: MessageType[] = ['BOT_RESPONSE', 'PENDING_RESPONSE']

type EventSourceStatus = 'connecting' | 'connected' | 'disconnected'

export const usePoll = (
  messages: MessageProps[],
  options?: {
    // How long in ms to poll when the connection is active.
    activePollingInterval?: number
    // How long in ms to poll when the connection is idle.
    idlePollingInterval?: number
    // How long in ms to wait after the last message from the bot before we consider the connection idle.
    activePollingBuffer?: number
  },
) => {
  const {
    activePollingInterval = ACTIVE_POLLING_INTERVAL,
    idlePollingInterval = IDLE_POLLING_INTERVAL,
    activePollingBuffer = 0,
  } = options ?? {}
  const revalidator = useRevalidator()

  const [pollingState, setPollingState] = useState<
    'active' | 'idle' | 'timeout'
  >('idle')

  // Used to handle when a new message is added after a timeout.
  const [prevNumMessages, setNumMessages] = useState(messages.length)

  // More messages will be sent after an internal model note.
  // Therefore, they are not terminal messages.
  const terminalMessages = (Array.isArray(messages) ? messages : []).filter(
    ({ messageType }) => messageType !== 'INTERNAL_MODEL_NOTE',
  )
  const finalMessage = terminalMessages.slice(-1)[0]

  const lastMessageIsNotBot =
    finalMessage && !BOT_MESSAGE_TYPES.includes(finalMessage.messageType)
  // In fast chat, bot may respond with multiple messages in succession, give it a buffer
  const hasCustomerMessage = messages.some(
    ({ messageType }) => messageType === 'CUSTOMER',
  )
  const botMayStillBeResponding =
    hasCustomerMessage &&
    !!finalMessage &&
    !!finalMessage.createdAt &&
    BOT_MESSAGE_TYPES.includes(finalMessage.messageType) &&
    Date.now() - new Date(finalMessage.createdAt).getTime() <
      activePollingBuffer

  const waitingOnBot = lastMessageIsNotBot || botMayStillBeResponding

  useEffect(() => {
    if (messages.length > prevNumMessages) {
      if (pollingState === 'timeout') {
        // Reset the polling state not to timeout.
        setPollingState(waitingOnBot ? 'active' : 'idle')
      }
      setNumMessages(messages.length)
    }
  }, [messages.length, pollingState, prevNumMessages, waitingOnBot])

  useEffect(() => {
    if (pollingState === 'timeout') {
      return
    }

    const timeout = setTimeout(() => {
      setPollingState('timeout')
    }, POLLING_TIMEOUT)

    return () => clearTimeout(timeout)
  }, [pollingState])

  useEffect(() => {
    const pollingDelay =
      pollingState === 'active' ? activePollingInterval : idlePollingInterval

    const interval = setInterval(() => {
      if (pollingState !== 'timeout') {
        revalidator.revalidate()
      }
    }, pollingDelay)

    return () => clearInterval(interval)
  }, [pollingState, revalidator, activePollingInterval, idlePollingInterval])

  useEffect(() => {
    if (pollingState === 'active' && !waitingOnBot) {
      setPollingState('idle')
    } else if (pollingState === 'idle' && waitingOnBot) {
      setPollingState('active')
    }
  }, [pollingState, waitingOnBot])

  return pollingState
}

const MAX_CONNECTION_ATTEMPT = 10
export const useEventSource = <T extends z.ZodTypeAny>(
  url: string,
  schema: T,
  {
    enabled = true,
    maxConnectionAttempts = MAX_CONNECTION_ATTEMPT,
  }: { enabled?: boolean; maxConnectionAttempts?: number } = {},
) => {
  const eventSource = useRef<EventSource>()
  const [messages, setMessages] = useState<z.infer<T>[]>([])
  const [status, setStatus] = useState<EventSourceStatus>('disconnected')
  const [_, setReconnectAttempts] = useState(0)

  const handleError = useCallback(
    (e: Event) => {
      console.error(e)
      captureException(e, {
        level: 'error',
        extra: {
          captureContext: 'useEventSource',
          url,
        },
      })

      // Because this is a closure, we need to use the functional form of setState
      // to ensure that we are using the latest value of reconnectAttempts.
      setReconnectAttempts((prev) => {
        if (prev + 1 >= maxConnectionAttempts) {
          return prev + 1
        }

        eventSource.current?.close()
        eventSource.current = undefined
        setupEventSource()
        return prev + 1
      })
    },
    [maxConnectionAttempts, url],
  )
  const handleMessage = useCallback(
    (e: MessageEvent) => {
      try {
        const rawData = JSON.parse(e.data)
        const parsedMessage = schema.parse(rawData)
        setMessages((prev) => [...prev, parsedMessage])
      } catch (error) {
        console.error(error)
        captureException(error, {
          level: 'error',
          extra: {
            captureContext: 'useEventSource',
            rawData: e.data,
            schema,
          },
        })
      }
    },
    [schema],
  )

  const setupEventSource = useCallback(() => {
    const source = new EventSource(url)
    setStatus('connecting')
    source.onopen = () => setStatus('connected')
    source.onerror = (e) => {
      setStatus('disconnected')
      handleError(e)
    }
    source.onmessage = handleMessage
    eventSource.current = source
    return source
  }, [handleError, handleMessage, url])

  useEffect(() => {
    setMessages([])
    setStatus('disconnected')
    if (eventSource.current) {
      eventSource.current.close()
      eventSource.current = undefined
    }
  }, [url])

  useEffect(() => {
    if (!enabled) {
      setMessages([])
      setStatus('disconnected')
      if (eventSource.current) {
        eventSource.current.close()
        eventSource.current = undefined
      }
      return
    }
    const source = setupEventSource()
    return () => {
      source.close()
      setStatus('disconnected')
    }
  }, [enabled, setupEventSource])

  return { eventSource, messages, status }
}

function useDedupedArray<T>(
  sourceA: T[],
  sourceB: T[],
  discriminator: (item: T) => string,
) {
  return useMemo(() => {
    const seen = new Set<string>()
    const result: T[] = []

    for (const item of [...sourceA, ...sourceB]) {
      const key = discriminator(item)
      if (!seen.has(key)) {
        seen.add(key)
        result.push(item)
      }
    }

    return result
  }, [sourceA, sourceB, discriminator])
}

// Given an SSE route where pointing to server's ticket SSE controller,
// create a connection and return a stream of ticket updates.
// For example `useSseTicketUpdates('/conversation/${ticketId}/sse')`
function useSseTicketUpdates(sseRoute: string, enabled: boolean) {
  const { messages: rawMessages, status } = useEventSource(
    sseRoute,
    TicketChannelPayloadSchema,
    { enabled },
  )

  const { messages, events, actions, actionError } = useMemo(() => {
    const sseEvents: TicketEventForQA[] = []
    const sseMessages: MessageDetails[] = []
    const sseActionsTaken: TicketActionResultForQA[] = []
    const messageChunks = new Map<string, MessageChunkPayload[]>()
    let sseActionError: TicketActionResultForQA | undefined

    for (const msg of rawMessages) {
      switch (msg.type) {
        case 'new-event':
          sseEvents.push(msg.event)
          break
        case 'new-message':
          sseMessages.push({
            ...msg.message,
            remoteCreatedAt: msg.message.remoteCreatedAt ?? undefined,
          })
          break
        case 'action-executed':
          if (msg.executionResult === 'success') {
            sseActionsTaken.push(msg.result)
          } else {
            sseActionError = msg.result
          }
          break
        case 'message-chunk':
          messageChunks.set(msg.messageId, [
            ...(messageChunks.get(msg.messageId) ?? []),
            msg,
          ])
          break
        case 'message-complete':
      }
    }

    const messagesWithChunks = sseMessages
      .map((msg) => {
        const chunks = messageChunks.get(msg.id)
        if (!chunks) {
          return msg
        }
        const content = chunks
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((chunk) => chunk.contentDelta)
          .join('')
        return { ...msg, content }
      })
      // If a placeholder message arrives with no content, ignore it.
      .filter((msg) => msg.content.length)

    return {
      messages: messagesWithChunks,
      events: sseEvents,
      actions: sseActionsTaken,
      actionError: sseActionError,
    }
  }, [rawMessages])

  return { messages, events, actions, actionError, status }
}

// Combine realtime data from SSE updates with data from the loader.
// Handles fallback to polling when SSE is not available. Produces a
// clean most up to date state that can be rendered. If you don't provide an sseRoute,
// polling will always be used instead.
export const useTicketUpdates = ({
  enableSse,
  sseRoute,
  loaderMessages,
  loaderEvents,
  loaderActions,
  callState = 'disconnected',
  responseStreamingEnabled = false,
}: {
  enableSse: boolean
  sseRoute: string
  loaderMessages: MessageDetails[]
  loaderEvents: TicketEventForQA[]
  loaderActions: TicketActionsResultsForQA
  callState?: 'connected' | 'disconnected'
  responseStreamingEnabled?: boolean
}) => {
  const sseData = useSseTicketUpdates(sseRoute, enableSse)
  const events = useDedupedArray(
    sseData.events,
    loaderEvents,
    (event) => event.id,
  )
  const messages = useDedupedArray(
    sseData.messages,
    loaderMessages,
    (msg) => msg.id,
  )
  const actionsTaken = useDedupedArray(
    sseData.actions,
    loaderActions.actionsTaken,
    (action) =>
      `${action.action.actionType}-${action.startTime}-${action.endTime}`,
  )
  const actions = useMemo(
    () => ({
      actionsTaken,
      actionError: sseData.actionError ?? loaderActions.actionError,
      skippedActions: loaderActions.skippedActions,
    }),
    [
      sseData.actionError,
      loaderActions.actionError,
      sseData.actions,
      loaderActions.skippedActions,
    ],
  )

  const pollingOptions = useMemo(() => {
    if (callState === 'connected') {
      return {
        activePollingInterval: 1_000,
        idlePollingInterval: 1_000,
        activePollingBuffer: responseStreamingEnabled ? 2_000 : undefined,
      }
    }

    if (sseData.status === 'connected') {
      return {
        activePollingInterval: 15_000,
        idlePollingInterval: 60_000,
        activePollingBuffer: 0,
      }
    }

    return {
      activePollingInterval: 1_000,
      idlePollingInterval: 10_000,
      activePollingBuffer: responseStreamingEnabled ? 2_000 : undefined,
    }
  }, [callState, sseData.status, responseStreamingEnabled])

  const pollingState = usePoll(loaderMessages, pollingOptions)

  return {
    messages,
    events,
    actions,
    pollingState,
  }
}
